<template>
  <form class="cart__form" action="/checkout" method="post">
    <CartHead
      :isEmpty="isEmpty"
      :cartData="cartData"
      :cartTotal="cartTotal"
      :hasFreeShipping="hasFreeShipping"
      :hasFreeGift="hasFreeGift"
      :freeShippingMin="freeShippingMin"
      :freeGiftMin="freeGiftMin"
      @toggle-cart="toggleCart"
    />
    <div class="cart__body">
      <div v-if="!isEmpty"  class="cart__inner">
        <div class="cart__items">
          <CartItem v-if="addingProduct" :placeholder="true" />
          <CartItem
            v-for="(item,index) in items"
            :cart="items"
            :key="item.key"
            :item="item"
          />
          <!-- Beam widget -->
          <div style="margin: 15px 0px 0px;" data-beam-widget="select-nonprofit">
            <beam-select-nonprofit
              apiKey="1h6d2pKq7jSo.e18689b8-a532-4405-b655-08c07b3e67f4"
              :storeId="481"
              lang="en"
            ></beam-select-nonprofit>
          </div>
        </div>
        <CartFooter
          v-if="!isEmpty && !isMobile"
          :cart="cart"
          :cartData="cartData"
          :cartHasSubscriptionProducts="cartHasSubscriptionProducts"
          :hasFreeShipping="hasFreeShipping"
        />
      </div>
      <CartRecs
        v-if="cartRecs.length"
        :cartRecs="cartRecs"
        :items="items"
      />
    </div>

    <CartFooter
      v-if="!isEmpty && isMobile"
      :cart="cart"
      :cartData="cartData"
      :cartHasSubscriptionProducts="cartHasSubscriptionProducts"
      :hasFreeShipping="hasFreeShipping"
    />

    <CartEmptyFooter 
      v-if="isEmpty"
      :cartData="cartData"
    />
  </form>
</template>

<script setup>
  import { ref, computed, onBeforeMount, onMounted, watch, toRefs } from 'vue';
  import {useStore} from "vuex";
  import cartHelpers from '../api'
  import CartHead from './cart-head.vue';
  import CartItem from './cart-item.vue';
  import CartFooter from './cart-footer.vue';
  import CartRecs from './cart-recs.vue';
  import CartEmptyFooter from './cart-empty-footer.vue';
  import windowResize from '../../../utils/window-resize';

  const props = defineProps({
    cartData: {
      type: Object,
      default: ({})
    },

    isCart: {
      type: Boolean,
      default: false
    },

    isCartOpen: {
      type: Boolean,
      default: false
    },
  })

  const { cartData } = toRefs(props)
  const { windowWidth } = windowResize()
  const { status } = cartHelpers;
  const store = useStore();
  var cbs = ref([])
  const cartRecs = ref([])
  const emit = defineEmits(['on-toggle'])
  
  onBeforeMount(async () => {
    // Setting window function for additem
    window.initReploCart = initReploCart;
    window.Additem = addItem;
    window.updateCartItems = update;
    window.cartItems = items.value;

    // Initialise store init function
    initCart();
    getCartRecs();
  })

  const cart = computed(() => store.state.cart)
  const totalCount = computed(() => store.state.cart.item_count)
  const cartStatus = computed(() => store.state.status)
  const isEmpty = computed(() => store.state.status === status.empty)
  const isMobile = computed(() => { return windowWidth.value < 1024;})

  const items = computed(() => {
    const items = cart.value.items || [];

    // Use a fusion of Stay code to filter out the Stay AI bundle products
    let obscuredCartDataNew = items
    .map((item, index) => {

      const itemPrice = (item.selling_plan_allocation !== undefined) ? Number(item.selling_plan_allocation.price) : Number(item.price);
      const itemCartDiscountedPrice = (item.discounted_price !== undefined) ? Number(item.discounted_price) : null;
      const itemCompareAtPrice = (item.selling_plan_allocation !== undefined) ? Number(item.selling_plan_allocation.compare_at_price) : null;

      return {
        itemID: item.id,
        itemTitle: item.title,
        itemHandle: item.handle,
        itemImage: item.image,
        itemPrice: ((itemCartDiscountedPrice !== null) && (itemCartDiscountedPrice < itemPrice)) ? itemCartDiscountedPrice : itemPrice,
        itemCompareAtPrice: ((itemCartDiscountedPrice !== null) && (itemCartDiscountedPrice < itemPrice)) ? itemPrice : itemCompareAtPrice,
        itemCartDiscountedPrice: (item.discounted_price !== undefined) ? item.discounted_price : false,
        itemQuantity: item.quantity,
        itemGrams: item.grams,
        itemUrl: item.url,
        itemKey: item.key,
        itemSellingPlanAllocation: (item.selling_plan_allocation !== undefined) ? true : false,
        itemPlanId: (item.selling_plan_allocation !== undefined) ? item.selling_plan_allocation.selling_plan.id : false,
        itemPlanType: (item.selling_plan_allocation !== undefined) ? item.selling_plan_allocation.selling_plan.name : false,
        itemBundleID: (item.properties !== null) ? ((item.properties.bundle_id !== undefined) ? item.properties.bundle_id : false) : false,
        isBundleChild: (item.properties !== null) ? ((item.properties.bundle_id !== undefined && item.properties.bundle_parent === undefined) ? true : false) : false,
        isBundleParent: (item.properties !== null) ? ((item.properties.bundle_parent === true) ? true : false) : false,
        bundleChildKeys: '',
        bundleChildQuantities: '',
        bundleItemList: '',
        bundleParentKey: '',
        itemTags: [],
        ...item,
        image: item.featured_image,
        line: index + 1,
      }
    });

    let removalIndexes = [];
    obscuredCartDataNew.forEach((mainItem) => {

        if(mainItem.isBundleParent) {

            const mainItemBundleID = mainItem.itemBundleID;
        
            if (mainItemBundleID) {
                let bundleChildKeys = [];
                let bundleChildQuantities = [];
                let bundleItemList = '';
            
                obscuredCartDataNew.forEach((comparedItem, i) => {
                    const comparedItemBundleID = comparedItem.itemBundleID;
                    
                    const comparedItemPrice = parseFloat(comparedItem.itemPrice) * parseFloat(comparedItem.itemQuantity);

                    let comparedItemCompareAtPrice = 0; // Default to 0 if null
                    if (comparedItem.itemCompareAtPrice !== null && comparedItem.itemCompareAtPrice !== undefined) {
                      comparedItemCompareAtPrice = parseFloat(comparedItem.itemCompareAtPrice) * parseFloat(comparedItem.itemQuantity);
                      if (isNaN(comparedItemCompareAtPrice)) {
                        comparedItemCompareAtPrice = 0; // Default to 0 if NaN
                      }
                    }
                    
                    if (comparedItem.isBundleParent === false && comparedItemBundleID === mainItemBundleID) {
                        bundleChildKeys.push(comparedItem.itemKey)
                        bundleChildQuantities.push(comparedItem.itemQuantity)
                        bundleItemList += '<li class="cart-item-bundle-el">' + comparedItem.itemTitle + (comparedItem.itemQuantity > 1 ? ' (' + comparedItem.itemQuantity + ')' : '') + '</li>';
                        
                        mainItem.itemPrice = parseFloat(parseFloat(mainItem.itemPrice) + comparedItemPrice);
                        
                        if (mainItem.itemCompareAtPrice !== null && mainItem.itemCompareAtPrice !== undefined) {
                          mainItem.itemCompareAtPrice = parseFloat(parseFloat(mainItem.itemCompareAtPrice) + comparedItemCompareAtPrice);
                        } else {
                          mainItem.itemCompareAtPrice = comparedItemCompareAtPrice;
                        }

                        removalIndexes.push(i);
                    }
                    if (comparedItem.isBundleParent === true && comparedItemBundleID === mainItemBundleID) {
                        bundleChildKeys.push(comparedItem.itemKey)
                        bundleChildQuantities.push(comparedItem.itemQuantity)
                        mainItem.bundleParentKey = comparedItem.itemKey;
                    }
                });
        
                mainItem.bundleChildKeys = bundleChildKeys;
                mainItem.bundleChildQuantities = bundleChildQuantities;
                mainItem.bundleItemList = (bundleItemList != '' ? '<ul class="cartItem__bundleEl cart-item-bundle-items">' + bundleItemList + '</ul>' : '');
            }
        }
    });

    obscuredCartDataNew = obscuredCartDataNew.filter((item, index) => {
        return removalIndexes.indexOf(index) == -1;
    });

    return obscuredCartDataNew;
  })

  const totalCartItems = computed(() => {
     return totalCount.value;
  })

  const addingProduct = computed(() =>{
    return cartStatus.value === status.adding;
  })

  const cartTotal = computed(() => {
    return (cart.value.total_price)
  })

  const hasFreeShipping = computed(() => {
    if (isGiftCardOnly.value) {
      return true;
    }

    if (cartHasSubscriptionProducts.value) {
      return cartTotal.value >= cartData.value.shippingRates.subscription.minimum;
    }

    return cartTotal.value >= cartData.value.shippingRates.onetime.minimum;
  })

  const hasFreeGift = computed(() => {
    if (cartHasSubscriptionProducts.value) {
      return cartTotal.value >= cartData.value.freeGift.subscription;
    }

    return cartTotal.value >= cartData.value.freeGift.onetime;
  })

  const freeShippingMin = computed(() => {
    if (cartHasSubscriptionProducts.value) {
      return cartData.value.shippingRates.subscription.minimum
    }

    return cartData.value.shippingRates.onetime.minimum;
  })

  const freeGiftMin = computed(() => {
    if (cartHasSubscriptionProducts.value) {
      return cartData.value.freeGift.subscription
    }

    return cartData.value.freeGift.onetime;
  })

  const cartHasSubscriptionProducts = computed(() => {
    return !isEmpty.value && items.value.some(item => item.selling_plan_allocation);
  })

  const isGiftCardOnly = computed(() => {
    return !isEmpty.value && items.value.every(item => item.gift_card === true);
  })

  const initCart = () => {
    store.dispatch("init")
  }

  const initReploCart = () => {
    store.dispatch("initReplo")
  }

  const getCartRecs = async () => {
    try {
      let pageUrl = encodeURI(window.location.href);

      const response = await fetch(
        `https://rebuyengine.com/api/v1/custom/id/142659?shopify_customer_id=1000652177482&key=7245kS%2FOMNx6PgVFOqxGCx%2FQuIaRD432VQzUSicSX4m6UKO9DSd7h65yPSEjGwR2AgL%2BO8TTj3jhympG86TIqg%3D%3D&format=pretty&limit=5&shopLocation=${window.shopLocation}&url=${pageUrl}`
      );

      // Check if HTTP response is successful
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Validate response data
      if (!data?.data) {
        throw new Error('Missing "data" field in API response');
      }

      // Assign data to reactive variable
      cartRecs.value = data.data;
    } catch (error) {
      console.log('test error fetching cart recommendations:', error);

      // Set default value to prevent undefined state
      cartRecs.value = [];
    }
  };

  const addItem = (data) => {
    const itemAddedToCart = (items.value || []).find(item =>
      item.variant_id === Number(data.id) &&
      (!data.properties || JSON.stringify(item.properties) === JSON.stringify(data.properties)) &&
      (!data.selling_plan 
        ? !item.selling_plan_allocation 
        : item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id === Number(data.selling_plan))
    );

    if(!itemAddedToCart){
      store.dispatch("add", data)
    }else{
      change({line: itemAddedToCart.line,quantity: itemAddedToCart.quantity + 1})
    }
  }

  const change = (data) => {
    store.dispatch("change", data);
  }

  const update = (data) => { 
    store.dispatch("update", data);
  }

  watch(cartStatus, (value) => {
    cbs.value.forEach((cb, index) => {
      cb(value);
      cbs.value.splice(index, 1);
    });
  })

  watch(totalCount, (count) => {
    const cartEl = document.querySelectorAll(".js-cart-count");
    cartEl.forEach((ele) => {
      if(count > 0) {
        ele.classList.add('active')
      }else {
        ele.classList.remove('active')
      }
    });
  })

  const toggleCart = () => {
    emit('on-toggle',false)
  }
</script>